export interface CastCrewMember {
  person_vtr_id?: string;
  name?: string;
  role?: string;
  sub_role?: string;
  character?: string;
  person_tmdb_id?: string;
  vtr_id?: string;
  meta?: {
    vtr_id?: string;
    role?: string;
    sub_role?: string;
    name?: string;
  };
}

// Type alias for compatibility with AssociatedPerson
export type AssociatedPerson = CastCrewMember;

export interface ValidationResult {
  isValid: boolean;
  message?: string;
  conflictingMember?: CastCrewMember;
}

function normalizeMember(member: any): { person_vtr_id: string, role: string, sub_role: string } {
  return {
    person_vtr_id: member.person_vtr_id || member.vtr_id || member.meta?.vtr_id || "",
    role: member.role || member.meta?.role || "",
    sub_role: member.sub_role || member.meta?.sub_role || ""
  };
}

/**
 * Validates if a cast and crew member can be added without creating duplicates
 * Based on the requirements:
 * Condition A: If the same person_vtr_id is being added:
 *   - Check the subrole:
 *     - If subrole is the same, then check the role:
 *       - If role is also the same → Duplicate entry → Not Allowed → Trigger validation error.
 *     - If subrole is different → Allowed (regardless of role).
 *     - If subrole is blank:
 *       - Check role:
 *         - If role is the same → Duplicate entry → Not Allowed → Trigger validation error.
 * Condition B: If role is different, the entry is Allowed, regardless of subrole.
 * 
 * @param newMember - The member to be added
 * @param existingMembers - Array of existing members
 * @param editingIndex - Optional index of member being edited (to exclude from validation)
 * @returns ValidationResult object with validation status and message
 */
export function validateCastCrewMember(
  newMember: CastCrewMember,
  existingMembers: CastCrewMember[],
  editingIndex?: number
): ValidationResult {
  const normNew = normalizeMember(newMember);
  if (!normNew.role) {
    return {
      isValid: false,
      message: 'Role is required for cast and crew members'
    };
  }
  if (!normNew.person_vtr_id) {
    return {
      isValid: false,
      message: 'Person VTR ID is required for cast and crew members'
    };
  }
  const membersToCheck = existingMembers.filter((_, index) => index !== editingIndex);
  for (const existingMember of membersToCheck) {
    const normExisting = normalizeMember(existingMember);
    if (
      normExisting.person_vtr_id === normNew.person_vtr_id &&
      normExisting.role === normNew.role &&
      normExisting.sub_role === normNew.sub_role
    ) {
      return {
        isValid: false,
        message: `Duplicate entry: ${normNew.person_vtr_id} already has the same role (${normNew.role}) and sub-role (${normNew.sub_role || 'none'})`,
        conflictingMember: existingMember
      };
    }
  }
  return { isValid: true };
}

/**
 * Validates the entire cast and crew list for duplicates
 * @param members - Array of cast and crew members
 * @returns ValidationResult object with validation status and message
 */
export function validateCastCrewList(members: CastCrewMember[]): ValidationResult {
  const validMembers = members.filter(member => member && (member.role || member.meta?.role) && (member.person_vtr_id || member.vtr_id || member.meta?.vtr_id));
  const duplicates: Array<{member: CastCrewMember, conflictingWith: CastCrewMember[]}> = [];
  for (let i = 0; i < validMembers.length; i++) {
    const member = validMembers[i];
    const normA = normalizeMember(member);
    const conflictingWith: CastCrewMember[] = [];
    for (let j = i + 1; j < validMembers.length; j++) {
      const otherMember = validMembers[j];
      const normB = normalizeMember(otherMember);
      if (
        normA.person_vtr_id &&
        normA.person_vtr_id === normB.person_vtr_id &&
        normA.role === normB.role &&
        normA.sub_role === normB.sub_role
      ) {
        conflictingWith.push(otherMember);
      }
    }
    if (conflictingWith.length > 0) {
      duplicates.push({ member, conflictingWith });
    }
  }
  if (duplicates.length > 0) {
    const firstDuplicate = duplicates[0];
    const conflictingNames = firstDuplicate.conflictingWith.map(m => m.name || m.meta?.name || 'Unknown Member').join(', ');
    const memberName = firstDuplicate.member.name || firstDuplicate.member.meta?.name || 'Unknown Member';
    const role = normalizeMember(firstDuplicate.member).role || 'Unknown Role';
    const subRole = normalizeMember(firstDuplicate.member).sub_role ? ` (${normalizeMember(firstDuplicate.member).sub_role})` : ' (no sub-role)';
    return {
      isValid: false,
      message: `Duplicate entries found in Cast & Crew. ${memberName} - ${role}${subRole} conflicts with: ${conflictingNames}`,
      conflictingMember: firstDuplicate.member
    };
  }
  return { isValid: true };
}

/**
 * Finds conflicting members for highlighting in the UI
 * @param members - Array of cast and crew members
 * @returns Array of member indices that have conflicts
 */
export function findConflictingMembers(members: CastCrewMember[]): number[] {
  const conflictingIndices: number[] = [];
  const validMembers = members.filter(member => member && member.role && member.person_vtr_id);
  
  for (let i = 0; i < validMembers.length; i++) {
    const member = validMembers[i];
    
    for (let j = i + 1; j < validMembers.length; j++) {
      const otherMember = validMembers[j];
      
      // Check if same person with same role and subrole
      if (member.person_vtr_id === otherMember.person_vtr_id && 
          member.role === otherMember.role && 
          member.sub_role === otherMember.sub_role) {
        conflictingIndices.push(i, j);
      }
    }
  }
  
  return [...new Set(conflictingIndices)];
}

/**
 * Creates mock data for testing validation scenarios
 * @returns Array of mock cast and crew members
 */
export function createMockCastCrewData(): CastCrewMember[] {
  return [
    {
      person_vtr_id: 'VTR001',
      name: 'John Doe',
      role: 'Actor',
      sub_role: 'Lead Actor',
      character: 'Hero',
      person_tmdb_id: 'TMDB001'
    },
    {
      person_vtr_id: 'VTR002',
      name: 'Jane Smith',
      role: 'Actor',
      sub_role: 'Supporting Actor',
      character: 'Villain',
      person_tmdb_id: 'TMDB002'
    },
    {
      person_vtr_id: 'VTR003',
      name: 'Bob Johnson',
      role: 'Director',
      character: '',
      person_tmdb_id: 'TMDB003'
    },
    {
      person_vtr_id: 'VTR004',
      name: 'Alice Brown',
      role: 'Producer',
      sub_role: 'Executive Producer',
      character: '',
      person_tmdb_id: 'TMDB004'
    }
  ];
}

/**
 * Test cases for validation logic based on the specified requirements
 */
export const testValidationScenarios = () => {
  const mockData = createMockCastCrewData();
  
  
  // Test Case 1: Add the same person with the same role and subrole → Should trigger validation error
  const testCase1 = {
    person_vtr_id: 'VTR001',
    name: 'John Doe',
    role: 'Actor',
    sub_role: 'Lead Actor',
    character: 'Hero Again'
  };
  
  const result1 = validateCastCrewMember(testCase1, mockData);
  
  // Test Case 2: Add the same person with a different subrole → Should be allowed
  const testCase2 = {
    person_vtr_id: 'VTR001',
    name: 'John Doe',
    role: 'Actor',
    sub_role: 'Supporting Actor',
    character: 'Sidekick'
  };
  
  const result2 = validateCastCrewMember(testCase2, mockData);
  console.log('Test Case 2 - Same person, different subrole:', result2);
  
  // Test Case 3: Add the same person with subrole as blank but same role → Should trigger validation error
  const testCase3 = {
    person_vtr_id: 'VTR003',
    name: 'Bob Johnson',
    role: 'Director',
    character: 'Co-Director'
  };
  
  const result3 = validateCastCrewMember(testCase3, mockData);
  console.log('Test Case 3 - Same person, blank subrole, same role:', result3);
  
  // Test Case 4: Add the same person with a different role → Should be allowed
  const testCase4 = {
    person_vtr_id: 'VTR001',
    name: 'John Doe',
    role: 'Producer',
    sub_role: 'Executive Producer',
    character: ''
  };
  
  const result4 = validateCastCrewMember(testCase4, mockData);
  console.log('Test Case 4 - Same person, different role:', result4);
  
  // Test Case 5: Add a completely new person → Should be allowed
  const testCase5 = {
    person_vtr_id: 'VTR005',
    name: 'Charlie Wilson',
    role: 'Cinematographer',
    character: ''
  };
  
  const result5 = validateCastCrewMember(testCase5, mockData);
  console.log('Test Case 5 - New person:', result5);
  
  // Test Case 6: Validate entire list
  const listValidation = validateCastCrewList(mockData);
  console.log('Test Case 6 - List validation:', listValidation);
  
  // Test Case 7: Find conflicting members
  const conflictingMembers = findConflictingMembers([
    ...mockData,
    testCase1 // This should create a conflict
  ]);
  console.log('Test Case 7 - Conflicting member indices:', conflictingMembers);
  
  return {
    testCase1: result1,
    testCase2: result2,
    testCase3: result3,
    testCase4: result4,
    testCase5: result5,
    listValidation,
    conflictingMembers
  };
};

/**
 * Helper function to run all test scenarios
 */
export const runAllValidationTests = () => {
  console.log('Running all Cast and Crew validation tests...');
  const results = testValidationScenarios();
  
  // Summary
  console.log('\n=== Test Summary ===');
  console.log(`Test Case 1 (Same person, same role/subrole): ${results.testCase1.isValid ? 'PASS' : 'FAIL'}`);
  console.log(`Test Case 2 (Same person, different subrole): ${results.testCase2.isValid ? 'PASS' : 'FAIL'}`);
  console.log(`Test Case 3 (Same person, blank subrole, same role): ${results.testCase3.isValid ? 'PASS' : 'FAIL'}`);
  console.log(`Test Case 4 (Same person, different role): ${results.testCase4.isValid ? 'PASS' : 'FAIL'}`);
  console.log(`Test Case 5 (New person): ${results.testCase5.isValid ? 'PASS' : 'FAIL'}`);
  console.log(`List Validation: ${results.listValidation.isValid ? 'PASS' : 'FAIL'}`);
  
  return results;
}; 